import { useHotelSlot } from "@b2bportal/core-hotels";
import { useHotelsStyles, useModuleBEM } from "@b2bportal/core-themes";
import {
  CoreHotelsComponent,
  Funnel,
  type PlatformComponentProps,
} from "@b2bportal/core-types";
import { getImageUrlsArray } from "@b2bportal/core-utilities";
import type { DateRange, Lodging } from "@b2bportal/lodging-api";
import { htmlDecode } from "@hopper-b2b/utilities";
import {
  DealTag,
  Features,
  HotelsRawEventName,
  ImageCarousel,
  Location,
  Rating,
  Tags,
  TripAdvisorRating,
  useEmitCoreHotelsRawEvent,
} from "@tenant/hotels";
import clsx from "clsx";

import defaultStyles from "./HotelCard.module.scss";
import { BonusEarnPill } from "@/modules/wallet";
import { useGetCurrentFunnel } from "@/utilities/hooks/useGetCurrentFunnel";

export type HotelCardProps = PlatformComponentProps & {
  hotel: Lodging;
  dateRange: DateRange;
  rooms?: number;
  onHover?: (hotelId: string | undefined) => void;
  /**
   * Force the stacked/mobile layout (image on top) regardless of viewport.
   */
  stacked?: boolean;
};

export const HotelCard = ({
  className,
  hotel,
  onHover,
  children,
  dateRange,
  rooms,
  stacked,
}: HotelCardProps) => {
  const styles = useHotelsStyles(CoreHotelsComponent.HotelCard, defaultStyles);
  const [block, cn] = useModuleBEM(styles, CoreHotelsComponent.HotelCard, {
    stacked: Boolean(stacked),
  });

  const HotelSlot = useHotelSlot();
  const isPackages = useGetCurrentFunnel() === Funnel.Package;

  const emitHotelSwipedCarousel = useEmitCoreHotelsRawEvent(
    HotelsRawEventName.HotelSwipedCarousel
  );

  const onNavigateToSlide = () => {
    emitHotelSwipedCarousel({ properties: { hotelId: hotel.lodging.id } });
  };

  return (
    <div
      className={clsx(block, className)}
      onMouseOver={() => onHover?.(hotel.lodging.id)}
      onMouseOut={() => onHover?.(undefined)}
      onFocus={() => onHover?.(hotel.lodging.id)}
      onBlur={() => onHover?.(undefined)}
    >
      <div className={cn("carousel-container")}>
        <ImageCarousel
          imageUrlsArray={getImageUrlsArray(hotel.lodging.media)}
          onNavigateToNextSlide={onNavigateToSlide}
          onNavigateToPrevSlide={onNavigateToSlide}
        />
        {!isPackages && (
          <BonusEarnPill variant="earn" className={cn("bonus-earn-pill")} />
        )}
      </div>
      <div className={cn("pre-title-container")}>
        <Rating className={cn("rating")} hotel={hotel} />
        <Location className={cn("location")} city={hotel.lodging.city} />
        <TripAdvisorRating className={cn("tripadvisor-rating")} hotel={hotel} />
      </div>
      <div className={cn("title-container")}>
        <h2 className={cn("title")}>
          {htmlDecode(hotel.lodging.name) ?? hotel.lodging.name}
        </h2>
      </div>
      <div className={cn("tags-container")}>
        <Tags hotel={hotel} />
      </div>
      <div className={cn("features-container")}>
        <Features hotel={hotel} />
      </div>
      <div className={cn("price-container")}>
        <HotelSlot.HotelCardPrice
          hotel={hotel}
          dateRange={dateRange}
          rooms={rooms}
        />
      </div>
      <div className={cn("deal-tag-container")}>
        <DealTag hotel={hotel} />
      </div>
      {children}
    </div>
  );
};
